import { cn } from "@/utils";
import { ChevronRight, type LucideIcon } from "lucide-react";

/**
 * Rail disclosure — the design system's SwapIcon idiom (rail-kit): an
 * expandable row's resource icon itself cross-fades to the disclosure
 * chevron on row hover, rotating 90° when open. Both glyphs share one box so
 * nothing shifts, and there is no reserved chevron column — leaf and branch
 * rows share one icon x by construction.
 *
 * Rotation source: pass `open` when the caller owns the state. Omit it for
 * rows whose open state lives on an ancestor's `data-state` (e.g. the Radix
 * accordion trigger in `RailGroup`). Every consumer's row root carries
 * `group/row`, which drives the hover swap.
 */
export function RailChevron({
  open,
  size = "size-3.5",
  className,
}: {
  open?: boolean;
  size?: string;
  className?: string;
}) {
  return (
    <ChevronRight
      aria-hidden
      className={cn(
        "shrink-0 text-n-50 transition-transform duration-150 ease-out",
        "opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100",
        size,
        open === undefined
          ? "group-data-[state=open]/row:rotate-90"
          : open && "rotate-90",
        className,
      )}
    />
  );
}

/**
 * Leading slot of an expandable row: the resource icon, hover-swapped to the
 * disclosure chevron (one box — the SwapIcon idiom).
 */
export function RailDisclosureIcon({
  open,
  icon: Icon,
  iconSize = "size-3.5",
  chevronSize,
}: {
  open?: boolean;
  icon: LucideIcon;
  iconSize?: string;
  /** Kept for call-site compatibility; the chevron fills the icon's box. */
  chevronSize?: string;
}) {
  void chevronSize;
  return (
    <span className={cn("relative inline-flex shrink-0", iconSize)}>
      <Icon
        className={cn(
          "absolute inset-0 text-n-50 transition-opacity duration-150 group-hover/row:opacity-0",
          iconSize,
        )}
      />
      <ChevronRight
        aria-hidden
        className={cn(
          "absolute inset-0 text-n-50 opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover/row:opacity-100",
          iconSize,
          open === undefined
            ? "group-data-[state=open]/row:rotate-90"
            : open && "rotate-90",
        )}
      />
    </span>
  );
}
